import Link from "next/link";
import { AppLogo } from "@/components/brand/app-logo";
import { TRADE_PAGES } from "@/lib/marketing/trade-pages";
import { PUBLISHED_COMPARE_PAGES } from "@/lib/marketing/compare-pages";
import { cn } from "@/lib/utils";

type SiteFooterProps = {
  /** `light` = home / marketing pages; `dark` = dark surfaces like /invite */
  variant?: "light" | "dark";
};

export function SiteFooter({ variant = "light" }: SiteFooterProps) {
  const dark = variant === "dark";

  return (
    <footer
      className={cn(
        "border-t py-10 sm:py-12",
        dark ? "border-[rgba(232,235,230,0.1)] bg-[#0e1419]" : "border-slate-200 bg-white"
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,2fr)] lg:gap-12">
          <div>
            <AppLogo href="/" tone={dark ? "marketingDark" : "marketing"} />
            <p
              className={cn(
                "mt-3 max-w-sm text-sm leading-relaxed",
                dark ? "text-[#9aa3a0]" : "text-slate-600"
              )}
            >
              Quotes and proposals for service businesses in the field.
            </p>
          </div>
          <div className="grid gap-8 sm:grid-cols-3">
            <div>
              <p
                className={cn(
                  "text-[11px] font-semibold uppercase tracking-[0.18em]",
                  dark ? "text-[#6b7370]" : "text-slate-500"
                )}
              >
                By trade
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                {TRADE_PAGES.map((t) => (
                  <li key={t.slug}>
                    <Link
                      href={`/for/${t.slug}`}
                      className={cn(
                        dark
                          ? "text-[#9aa3a0] hover:text-[#e8ebe6] hover:underline"
                          : "text-slate-600 hover:text-slate-900 hover:underline"
                      )}
                    >
                      {t.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p
                className={cn(
                  "text-[11px] font-semibold uppercase tracking-[0.18em]",
                  dark ? "text-[#6b7370]" : "text-slate-500"
                )}
              >
                Compare
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                {PUBLISHED_COMPARE_PAGES.map((p) => (
                  <li key={p.slug}>
                    <Link
                      href={`/compare/${p.slug}`}
                      className={cn(
                        dark
                          ? "text-[#9aa3a0] hover:text-[#e8ebe6] hover:underline"
                          : "text-slate-600 hover:text-slate-900 hover:underline"
                      )}
                    >
                      vs {p.competitorName}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p
                className={cn(
                  "text-[11px] font-semibold uppercase tracking-[0.18em]",
                  dark ? "text-[#6b7370]" : "text-slate-500"
                )}
              >
                Company
              </p>
              <ul
                className={cn(
                  "mt-3 space-y-2 text-sm font-medium",
                  dark ? "text-[#9aa3a0]" : "text-slate-600"
                )}
              >
                <li>
                  <Link
                    href="/guides"
                    className={dark ? "hover:text-[#e8ebe6]" : "hover:text-slate-900"}
                  >
                    Guides
                  </Link>
                </li>
                <li>
                  <Link
                    href="/legal/privacy"
                    className={dark ? "hover:text-[#e8ebe6]" : "hover:text-slate-900"}
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    href="/legal/terms"
                    className={dark ? "hover:text-[#e8ebe6]" : "hover:text-slate-900"}
                  >
                    Terms
                  </Link>
                </li>
                <li>
                  <Link
                    href="/legal/accessibility"
                    className={dark ? "hover:text-[#e8ebe6]" : "hover:text-slate-900"}
                  >
                    Accessibility
                  </Link>
                </li>
                <li>
                  <Link
                    href="/contact"
                    className={dark ? "hover:text-[#e8ebe6]" : "hover:text-slate-900"}
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    href="/login?force=1"
                    className={dark ? "hover:text-[#e8ebe6]" : "hover:text-slate-900"}
                  >
                    Sign in
                  </Link>
                </li>
                <li>
                  <Link
                    href="/signup"
                    className={dark ? "hover:text-[#e8ebe6]" : "hover:text-slate-900"}
                  >
                    Sign up
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <p
          className={cn(
            "mt-12 border-t pt-8 text-center text-xs",
            dark
              ? "border-[rgba(232,235,230,0.1)] text-[#6b7370]"
              : "border-slate-100 text-slate-500"
          )}
        >
          © {new Date().getFullYear()} Jorvio Quotes. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
